import { useState, useEffect, useCallback } from 'react';
import { Loader2, Sparkles, Database, Hash, Type, AlertTriangle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  tariffCatalogService,
  type RespuestaHistorial,
  type FilaHistorial,
  type CaminoBusqueda,
} from '../services/tariff-catalog-service';

const TAMANO_PAGINA = 50;

/** Etiquetas en lenguaje llano: "OR_FALLBACK" no le dice nada a nadie. */
const CAMINO_INFO: Record<
  CaminoBusqueda,
  { label: string; icon: React.ElementType; clase: string; ayuda: string }
> = {
  CODIGO: {
    label: 'Por código',
    icon: Hash,
    clase: 'border-info-borde bg-info-suave text-info ',
    ayuda: 'Se escribió un código arancelario — no cuesta nada.',
  },
  TEXTO_SIMPLE: {
    label: 'Por texto',
    icon: Type,
    clase: 'border-border bg-muted text-muted-foreground ',
    ayuda: 'Las palabras se buscaron tal cual en el arancel — no cuesta nada.',
  },
  IA: {
    label: 'Con intérprete IA',
    icon: Sparkles,
    clase:
      'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-500/40 dark:bg-violet-500/10 dark:text-violet-300',
    ayuda: 'La descripción se tradujo a vocabulario legal antes de buscar.',
  },
  OR_FALLBACK: {
    label: 'Aproximado',
    icon: AlertTriangle,
    clase: 'border-alerta-borde bg-alerta-suave text-alerta ',
    ayuda: 'La búsqueda exacta no encontró nada y se cayó a la red de seguridad.',
  },
};

/** Los costos son centésimas de centavo: con 2 decimales todo daría "$0.00". */
function fmtUsd(v: number, decimales = 4): string {
  return `$${v.toFixed(decimales)}`;
}

function fmtFecha(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('es-BO', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Indicador({
  titulo,
  valor,
  detalle,
}: {
  titulo: string;
  valor: string;
  detalle?: string;
}) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-xs text-muted-foreground">{titulo}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{valor}</p>
      {detalle && <p className="mt-1 text-xs text-muted-foreground">{detalle}</p>}
    </div>
  );
}

function BadgeCamino({ camino }: { camino: CaminoBusqueda }) {
  const info = CAMINO_INFO[camino] ?? {
    label: camino,
    icon: Type,
    clase: 'border-border bg-muted text-muted-foreground',
    ayuda: '',
  };
  const Icono = info.icon;
  return (
    <Badge variant="outline" className={`gap-1 whitespace-nowrap ${info.clase}`} title={info.ayuda}>
      <Icono className="h-3 w-3" />
      {info.label}
    </Badge>
  );
}

export function VistaHistorial() {
  const [data, setData] = useState<RespuestaHistorial | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [camino, setCamino] = useState<CaminoBusqueda | 'TODOS'>('TODOS');

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      setData(
        await tariffCatalogService.historial({
          desde: desde || undefined,
          hasta: hasta || undefined,
          camino: camino === 'TODOS' ? undefined : camino,
          page,
          pageSize: TAMANO_PAGINA,
        })
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar el historial.');
      setData(null);
    } finally {
      setCargando(false);
    }
  }, [desde, hasta, camino, page]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const resumen = data?.resumen;
  const totalPaginas = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-muted-foreground">
        Cada búsqueda que se hizo en el Asistente, con qué camino la resolvió y cuánto costó. Sirve
        para dos cosas: controlar el gasto del intérprete IA, y ver qué busca la gente — si una
        consulta aparece seguido y devuelve poco, ahí hay un sinónimo o una línea de glosario que
        falta.
      </p>

      <div className="flex flex-wrap items-end gap-3 rounded-md border p-4">
        <div className="space-y-2">
          <Label htmlFor="hist-desde">Desde</Label>
          <Input
            id="hist-desde"
            type="date"
            value={desde}
            onChange={(e) => {
              setPage(1);
              setDesde(e.target.value);
            }}
            className="w-40"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="hist-hasta">Hasta</Label>
          <Input
            id="hist-hasta"
            type="date"
            value={hasta}
            onChange={(e) => {
              setPage(1);
              setHasta(e.target.value);
            }}
            className="w-40"
          />
        </div>
        <div className="space-y-2">
          <Label>Camino</Label>
          <Select
            value={camino}
            onValueChange={(v) => {
              setPage(1);
              setCamino(v as CaminoBusqueda | 'TODOS');
            }}
          >
            <SelectTrigger className="w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="TODOS">Todos</SelectItem>
              {(Object.keys(CAMINO_INFO) as CaminoBusqueda[]).map((c) => (
                <SelectItem key={c} value={c}>
                  {CAMINO_INFO[c].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {(desde || hasta || camino !== 'TODOS') && (
          <Button
            variant="ghost"
            onClick={() => {
              setPage(1);
              setDesde('');
              setHasta('');
              setCamino('TODOS');
            }}
          >
            Limpiar filtros
          </Button>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <span>{error}</span>
        </div>
      )}

      {resumen && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Indicador
              titulo="Consultas"
              valor={String(resumen.consultas)}
              detalle={`${resumen.consultasIa} usaron el intérprete IA`}
            />
            <Indicador
              titulo="Gasto del período"
              valor={fmtUsd(resumen.gastoUsd)}
              detalle="Solo las llamadas que se pagaron"
            />
            <Indicador
              titulo="Resueltas por cache"
              valor={
                resumen.consultasIa > 0 ? `${resumen.cachePorcentaje.toFixed(0)}%` : 'Sin datos'
              }
              // El denominador se dice explícitamente: sobre el total daría un
              // número bajo solo porque muchas búsquedas son por código, y el
              // cache se vería peor de lo que es.
              detalle={
                resumen.consultasIa > 0
                  ? `${resumen.cacheHits} de ${resumen.consultasIa} consultas con IA`
                  : 'Todavía no hubo consultas con IA'
              }
            />
            <Indicador
              titulo="Costo promedio con IA"
              valor={resumen.costoPromedioIa > 0 ? fmtUsd(resumen.costoPromedioIa, 6) : '—'}
              detalle={
                resumen.costoPromedioIa > 0
                  ? 'Por consulta que sí pagó llamada'
                  : 'Ninguna consulta se pagó en este período'
              }
            />
          </div>

          {resumen.cacheHits > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-ok-borde bg-ok-suave p-3 text-sm ">
              <Database className="mt-0.5 h-4 w-4 shrink-0 text-ok " />
              {resumen.costoPromedioIa > 0 ? (
                <span>
                  Ahorro <strong>estimado</strong> por el cache:{' '}
                  <strong className="tabular-nums">{fmtUsd(resumen.ahorroEstimadoUsd)}</strong>. Es
                  una estimación, no un dato exacto: se calcula como las {resumen.cacheHits}{' '}
                  consultas que respondió el cache por el costo promedio del período. No sabemos qué
                  habría costado cada una en particular, porque depende de su largo.
                </span>
              ) : (
                /* Sin ninguna consulta pagada en el período no hay promedio con
                   el que estimar, y mostrar "$0.0000" se leería como "el cache
                   no ahorró nada" — exactamente lo contrario de lo que pasó. */
                <span>
                  El cache respondió <strong>{resumen.cacheHits}</strong>{' '}
                  {resumen.cacheHits === 1 ? 'consulta' : 'consultas'} sin pagar ninguna llamada. En
                  este período no se puede estimar cuánto se ahorró, porque el promedio se calcula
                  con las consultas que sí se pagaron y no hubo ninguna: todas salieron del cache.
                </span>
              )}
            </div>
          )}
        </>
      )}

      {cargando ? (
        <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando el historial…
        </div>
      ) : data && data.filas.length === 0 ? (
        <div className="flex items-start gap-2 rounded-md border p-4 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {desde || hasta || camino !== 'TODOS'
              ? 'No hay búsquedas que cumplan estos filtros. Probá ampliando el rango de fechas.'
              : 'Todavía no se registró ninguna búsqueda. Las que se hagan desde “Explorar el arancel” van a aparecer acá.'}
          </span>
        </div>
      ) : (
        data && (
          <>
            {/* Scroll propio + encabezado sticky. El `[&>div]:overflow-visible`
                anula el `overflow-auto` que trae el wrapper del Table de shadcn:
                si queda, ese wrapper se vuelve el scroll-container y el sticky
                del `th` no funciona (lección del vault 102). */}
            <div className="max-h-[480px] overflow-auto rounded-md border [&>div]:overflow-visible">
              {/* `table-fixed` es necesario, no cosmético: con el layout
                  automático el navegador trata los anchos como sugerencias y la
                  columna "Qué entendió la IA" —texto largo— estiraba la tabla
                  hasta empujar Costo fuera de la pantalla. */}
              <Table className="table-fixed">
                {/* Costo va ANTES de Partidas, no al final. Medido en pantalla
                    de 1280: la tabla entra completa (no hay desborde), pero los
                    botones flotantes de ayuda/bug/chat viven fijos en el borde
                    derecho justo a la altura de las primeras filas y tapaban la
                    última columna. Se corre el dato por el que existe el panel
                    fuera de esa zona; Partidas tolera quedar debajo. */}
                <TableHeader className="sticky top-0 z-10 bg-background">
                  <TableRow>
                    <TableHead className="w-[104px] whitespace-nowrap">Fecha</TableHead>
                    <TableHead className="w-[200px]">Consulta</TableHead>
                    <TableHead className="w-[170px]">Camino</TableHead>
                    <TableHead className="w-[220px]">Qué entendió la IA</TableHead>
                    <TableHead className="w-[92px] whitespace-nowrap text-right">Costo</TableHead>
                    <TableHead className="w-[84px] text-right">Partidas</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.filas.map((f: FilaHistorial) => (
                    <TableRow key={f.id}>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
                        {fmtFecha(f.createdAt)}
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        <span className="block truncate font-medium" title={f.consulta}>
                          {f.consulta}
                        </span>
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {f.latencyMs} ms
                        </span>
                        {(f.filtroCapitulo || f.filtroIce) && (
                          <span className="mt-0.5 flex flex-wrap gap-1">
                            {f.filtroCapitulo && (
                              <Badge
                                variant="outline"
                                className="whitespace-nowrap"
                                title="La búsqueda corrió con este filtro puesto: pocos o cero resultados acá no significan que el buscador falló."
                              >
                                filtro: cap. {f.filtroCapitulo}
                              </Badge>
                            )}
                            {f.filtroIce && (
                              <Badge variant="outline" className="whitespace-nowrap">
                                filtro: solo ICE
                              </Badge>
                            )}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          <BadgeCamino camino={f.camino} />
                          {f.cacheHit && (
                            <Badge
                              variant="outline"
                              className="gap-1 whitespace-nowrap border-ok-borde bg-ok-suave text-ok "
                              title="La respondió el cache: no se pagó llamada a la API."
                            >
                              <Database className="h-3 w-3" />
                              cache
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[220px]">
                        {f.interpretacion ? (
                          <div className="space-y-1">
                            <span className="block text-sm">{f.interpretacion.concepto}</span>
                            <span className="block text-xs text-muted-foreground">
                              {f.interpretacion.terminosLegales.join(' · ')}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            No pasó por el intérprete
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap">
                        {f.costUsd > 0 ? (
                          fmtUsd(f.costUsd, 6)
                        ) : (
                          <span className="text-muted-foreground">gratis</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{f.resultados}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {data.total} {data.total === 1 ? 'búsqueda' : 'búsquedas'} · página {data.page} de{' '}
                {totalPaginas}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={data.page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={data.page >= totalPaginas}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
}
